import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  TablePagination,
  InputAdornment,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import RefreshIcon from '@material-ui/icons/Refresh';
import SearchIcon from '@material-ui/icons/Search';
import FilterListIcon from '@material-ui/icons/FilterList';
import { useApi, identityApiRef, configApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Header, Page, Content } from '@backstage/core-components';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  addButton: {
    marginTop: theme.spacing(2),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  filterSection: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  filterGrid: {
    marginTop: theme.spacing(2),
  },
}));

interface OpenFgaTuple {
  user: string;
  relation: string;
  object: string;
}

export const ManageRoles = () => {
  const classes = useStyles();
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const config = useApi(configApiRef);

  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [allTuples, setAllTuples] = useState<OpenFgaTuple[]>([]);
  const [filteredTuples, setFilteredTuples] = useState<OpenFgaTuple[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Filters
  const [searchText, setSearchText] = useState('');
  const [filterRelation, setFilterRelation] = useState('');
  const [filterObjectType, setFilterObjectType] = useState('');
  const [filterUserType, setFilterUserType] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Form fields for new tuple
  const [newUser, setNewUser] = useState('');
  const [newRelation, setNewRelation] = useState('');
  const [newObject, setNewObject] = useState('');

  // Common relations
  const commonRelations = [
    'platform_admin',
    'division_manager',
    'division_devops',
    'manager',
    'delegate',
    'requester',
    'devops_cross',
    'jp',
    'developer',
    'devops',
    'member',
    'business_manager',
    'it_owner',
    'projects',
    'parent',
    'division_parent'
  ];

  // Object types for filtering
  const objectTypes = [
    'platform',
    'division',
    'project',
    'application',
    'component',
    'user',
    'group',
    'request'
  ];

  // User types for filtering
  const userTypes = [
    'user',
    'platform',
    'project',
    'application',
  ];

  // Check if user is platform_admin
  useEffect(() => {
    let cancelled = false;

    const checkPlatformAdminPermission = async () => {
      try {
        setAuthLoading(true);

        // Get REAL user identity (not impersonated)
        const identity = await identityApi.getBackstageIdentity();
        const userEntityRef = identity.userEntityRef;
        const realUserName = userEntityRef.split('/')[1];

        // Get user email from catalog
        let realUserEmail = realUserName; // Fallback to username
        try {
          const userEntity = await catalogApi.getEntityByRef(userEntityRef);
          const profile = userEntity?.spec?.profile as any;
          if (profile?.email) {
            realUserEmail = profile.email;
          }
        } catch (err) {
          console.warn('⚠️ Could not fetch user entity for admin check, using username instead:', err);
        }

        // Check platform_admin relation on platform:main
        const resource = 'platform:main';

        console.log('🔐 Checking platform_admin permission for Manage Roles:', {
          user: `user:${realUserEmail}`,
          relation: 'platform_admin',
          resource: resource,
        });

        // Call backend API using REAL user (not impersonated)
        const backendUrl = config.getString('backend.baseUrl');
        const token = (await identityApi.getCredentials()).token;
        
        const response = await fetch(`${backendUrl}/api/openfga-client/check-permission`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            user: `user:${realUserEmail}`,  // Using REAL user email, NOT impersonated
            action: 'platform_admin',
            resource: resource,
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to check permission: ${response.statusText}`);
        }

        const data = await response.json();
        
        if (!cancelled) {
          setIsPlatformAdmin(data.allowed || false);
          setAuthLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error checking platform_admin permission:', err);
          setIsPlatformAdmin(false);
          setAuthLoading(false);
        }
      }
    };

    checkPlatformAdminPermission();

    return () => {
      cancelled = true;
    };
  }, [identityApi, catalogApi, config]);

  const loadTuples = async () => {
    try {
      setLoading(true);
      setError(null);

      const backendUrl = config.getString('backend.baseUrl');
      const token = (await identityApi.getCredentials()).token;

      const response = await fetch(`${backendUrl}/api/openfga-client/all-tuples`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error(`Failed to load tuples: ${response.statusText}`);
      }

      const data = await response.json();
      setAllTuples(data.tuples || []);
      setPage(0); // Reset to first page when reloading
    } catch (err: any) {
      setError(err.message || 'Failed to load tuples');
    } finally {
      setLoading(false);
    }
  };

  // Apply filters
  useEffect(() => {
    let filtered = [...allTuples];

    // Search text filter (searches in user, relation, and object)
    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(
        tuple =>
          tuple.user.toLowerCase().includes(search) ||
          tuple.relation.toLowerCase().includes(search) ||
          tuple.object.toLowerCase().includes(search),
      );
    }

    // Relation filter
    if (filterRelation) {
      filtered = filtered.filter(tuple => tuple.relation === filterRelation);
    }

    // Object type filter
    if (filterObjectType) {
      filtered = filtered.filter(tuple => tuple.object.startsWith(`${filterObjectType}:`));
    }

    // User type filter
    if (filterUserType) {
      filtered = filtered.filter(tuple => tuple.user.startsWith(`${filterUserType}:`));
    }

    setFilteredTuples(filtered);
    setPage(0); // Reset to first page when filters change
  }, [allTuples, searchText, filterRelation, filterObjectType, filterUserType]);

  useEffect(() => {
    // Only load tuples if user is platform_admin
    if (isPlatformAdmin && !authLoading) {
      loadTuples();
    }
  }, [isPlatformAdmin, authLoading]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const clearFilters = () => {
    setSearchText('');
    setFilterRelation('');
    setFilterObjectType('');
    setFilterUserType('');
  };

  const handleAddTuple = async () => {
    try {
      setError(null);
      setSuccess(null);

      if (!newUser || !newRelation || !newObject) {
        setError('All fields are required');
        return;
      }

      const backendUrl = config.getString('backend.baseUrl');
      const token = (await identityApi.getCredentials()).token;

      const tuple = {
        user: newUser,
        relation: newRelation,
        object: newObject,
      };

      const response = await fetch(`${backendUrl}/api/openfga-client/add-tuple`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(tuple),
      });

      if (!response.ok) {
        throw new Error(`Failed to add tuple: ${response.statusText}`);
      }

      setSuccess('Tuple added successfully');
      setOpenDialog(false);
      setNewUser('');
      setNewRelation('');
      setNewObject('');
      loadTuples();
    } catch (err: any) {
      setError(err.message || 'Failed to add tuple');
    }
  };

  const handleDeleteTuple = async (tuple: OpenFgaTuple) => {
    try {
      setError(null);
      setSuccess(null);

      const backendUrl = config.getString('backend.baseUrl');
      const token = (await identityApi.getCredentials()).token;

      const response = await fetch(`${backendUrl}/api/openfga-client/remove-tuple`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(tuple),
      });

      if (!response.ok) {
        throw new Error(`Failed to delete tuple: ${response.statusText}`);
      }

      setSuccess('Tuple deleted successfully');
      loadTuples();
    } catch (err: any) {
      setError(err.message || 'Failed to delete tuple');
    }
  };

  return (
    <Page themeId="tool">
      <Header title="Manage Roles" subtitle="Manage OpenFGA Authorization Tuples" />
      <Content>
        {authLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : !isPlatformAdmin ? (
          <Box className={classes.root}>
            <Alert severity="error">
              <Typography variant="h6">Access Denied</Typography>
              <Typography variant="body2">
                You do not have permission to access this page. Only platform administrators can manage roles.
              </Typography>
            </Alert>
          </Box>
        ) : (
          <Box className={classes.root}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)} style={{ marginBottom: 16 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" onClose={() => setSuccess(null)} style={{ marginBottom: 16 }}>
                {success}
              </Alert>
            )}

            <Card className={classes.section}>
            <CardContent>
              <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>
                  <Typography variant="h5">Authorization Tuples</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Showing {filteredTuples.length} of {allTuples.length} tuples
                  </Typography>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={loadTuples}
                    disabled={loading}
                    style={{ marginRight: 8 }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenDialog(true)}
                  >
                    Add Tuple
                  </Button>
                </Grid>
              </Grid>

              {/* Filter Section */}
              <Box mt={2} mb={2}>
                <Button
                  startIcon={<FilterListIcon />}
                  onClick={() => setShowFilters(!showFilters)}
                  variant="outlined"
                  size="small"
                >
                  Filters {showFilters ? '▲' : '▼'}
                </Button>
                {showFilters && (
                  <Box mt={2}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Search"
                          value={searchText}
                          onChange={e => setSearchText(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon />
                              </InputAdornment>
                            ),
                          }}
                          placeholder="Search user, relation, or object"
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Relation</InputLabel>
                          <Select
                            value={filterRelation}
                            onChange={e => setFilterRelation(e.target.value as string)}
                          >
                            <MenuItem value="">All</MenuItem>
                            {commonRelations.map(relation => (
                              <MenuItem key={relation} value={relation}>
                                {relation}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Object Type</InputLabel>
                          <Select
                            value={filterObjectType}
                            onChange={e => setFilterObjectType(e.target.value as string)}
                          >
                            <MenuItem value="">All</MenuItem>
                            {objectTypes.map(type => (
                              <MenuItem key={type} value={type}>
                                {type}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <FormControl fullWidth size="small">
                          <InputLabel>User Type</InputLabel>
                          <Select
                            value={filterUserType}
                            onChange={e => setFilterUserType(e.target.value as string)}
                          >
                            <MenuItem value="">All</MenuItem>
                            {userTypes.map(type => (
                              <MenuItem key={type} value={type}>
                                {type}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Button fullWidth variant="outlined" onClick={clearFilters} size="small">
                          Clear Filters
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Box>

              <TableContainer component={Paper} className={classes.tableContainer}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Relation</TableCell>
                      <TableCell>Object</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          Loading...
                        </TableCell>
                      </TableRow>
                    ) : filteredTuples.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No tuples found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredTuples
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((tuple, index) => (
                          <TableRow key={`${tuple.user}-${tuple.relation}-${tuple.object}-${index}`}>
                            <TableCell>{tuple.user}</TableCell>
                            <TableCell>
                              <Chip label={tuple.relation} className={classes.chip} size="small" />
                            </TableCell>
                            <TableCell>{tuple.object}</TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteTuple(tuple)}
                                title="Delete tuple"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                component="div"
                count={filteredTuples.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[25, 50, 100, 200]}
              />
            </CardContent>
          </Card>

          {/* Add Tuple Dialog */}
          <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Add New Tuple</DialogTitle>
            <DialogContent>
              <TextField
                label="User"
                fullWidth
                value={newUser}
                onChange={e => setNewUser(e.target.value)}
                placeholder="user:email@example.com"
                className={classes.formField}
                helperText="Format: user:{email} or platform:main"
              />
              <FormControl fullWidth className={classes.formField}>
                <InputLabel>Relation</InputLabel>
                <Select
                  value={newRelation}
                  onChange={e => setNewRelation(e.target.value as string)}
                >
                  {commonRelations.map(rel => (
                    <MenuItem key={rel} value={rel}>
                      {rel}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Object"
                fullWidth
                value={newObject}
                onChange={e => setNewObject(e.target.value)}
                placeholder="platform:main, project:PRJ001, application:APM001"
                className={classes.formField}
                helperText="Format: {type}:{id}"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
              <Button onClick={handleAddTuple} color="primary" variant="contained">
                Add
              </Button>
            </DialogActions>
          </Dialog>
          </Box>
        )}
      </Content>
    </Page>
  );
};
